import { useState } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import TwoFactorCard from "../components/TwoFactorCard";
import { useT } from "../i18n";
import { useLangStore, type Lang } from "../i18n/langStore";

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div style={{
      padding: "0.65rem 1rem",
      borderRadius: 12,
      background: "var(--clr-surface)",
      border: "1px solid var(--clr-border)",
      backdropFilter: "var(--clr-blur)",
      WebkitBackdropFilter: "var(--clr-blur)",
      boxShadow: "var(--clr-shadow-sm)",
      color: "var(--clr-text2)",
      fontSize: "0.85rem",
      fontWeight: 500,
      marginTop: "1rem",
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 4, background: ok ? "#22c55e" : "#ef4444", display: "inline-block", flexShrink: 0 }} />
      {msg}
    </div>
  );
}

export default function ProfilePage() {
  const t = useT();
  const lang = useLangStore(s => s.lang);
  const setLang = useLangStore(s => s.setLang);

  // Cambia el idioma al vuelo (UI) y lo persiste en la BD (best-effort), para
  // que las notificaciones y los emails que genera el backend salgan en él.
  function changeLang(l: Lang) {
    setLang(l);
    api.put("/auth/me", { lang: l }).catch(() => { /* la UI ya cambió; reintenta al próximo cambio */ });
  }
  const { user, updateProfile } = useAuthStore();
  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [loading, setLoading] = useState(false);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setToast(null);

    // Validate password fields
    if (newPwd || currentPwd || confirmPwd) {
      if (!currentPwd) {
        showToast(t("profile.needCurrentPwd"), false);
        setLoading(false);
        return;
      }
      if (newPwd.length < 8) {
        showToast(t("profile.pwdTooShort"), false);
        setLoading(false);
        return;
      }
      if (newPwd !== confirmPwd) {
        showToast(t("profile.pwdMismatch"), false);
        setLoading(false);
        return;
      }
    }

    const body: Record<string, string | null> = {
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
    };

    if (currentPwd && newPwd) {
      body.current_password = currentPwd;
      body.new_password = newPwd;
    }

    try {
      const result = await api.put<{ first_name: string | null; last_name: string | null; email: string }>(
        "/auth/me",
        body
      );
      updateProfile(result.first_name, result.last_name);
      // Clear password fields
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      showToast(t("profile.saved"), true);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t("profile.saveError"), false);
    } finally {
      setLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.55rem 0.85rem",
    background: "var(--clr-input-bg)",
    border: "1px solid var(--clr-border)",
    borderRadius: 12,
    color: "var(--clr-text)",
    fontSize: "0.875rem",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "var(--clr-text3)",
    marginBottom: "0.3rem",
    display: "block",
    letterSpacing: "0.3px",
  };

  const sectionTitle: React.CSSProperties = {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "1.1px",
    textTransform: "uppercase",
    color: "var(--clr-text4)",
    marginBottom: "1rem",
    paddingBottom: "0.5rem",
    borderBottom: "1px solid var(--clr-border2)",
  };

  const cardStyle: React.CSSProperties = {
    background: "var(--clr-surface)",
    border: "1px solid var(--clr-border)",
    borderRadius: 18,
    backdropFilter: "var(--clr-blur)",
    WebkitBackdropFilter: "var(--clr-blur)",
    boxShadow: "var(--clr-shadow-sm)",
    padding: "1.5rem",
    marginBottom: "1.25rem",
  };

  return (
    <div style={{ padding: "2rem", maxWidth: 560, margin: "0 auto" }}>
      <h1 style={{ fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", marginBottom: "1.75rem" }}>
        {t("profile.title")}
      </h1>

      <form onSubmit={handleSave}>
        {/* Personal info card */}
        <div style={cardStyle}>
          <div style={sectionTitle}>{t("profile.personalInfo")}</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem", marginBottom: "0.9rem" }}>
            <div>
              <label style={labelStyle}>{t("profile.firstName")}</label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder={t("profile.firstNamePlaceholder")}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("profile.lastName")}</label>
              <input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder={t("profile.lastNamePlaceholder")}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>{t("profile.email")}</label>
            <div style={{
              padding: "0.55rem 0.85rem",
              background: "var(--clr-input-bg)",
              border: "1px solid var(--clr-border)",
              borderRadius: 12,
              color: "var(--clr-text3)",
              fontSize: "0.875rem",
            }}>
              {user?.email}
            </div>
            <p style={{ fontSize: "0.73rem", color: "var(--clr-text4)", marginTop: "0.3rem" }}>
              {t("profile.emailHelp")}
            </p>
          </div>
        </div>

        {/* Password card */}
        <div style={cardStyle}>
          <div style={sectionTitle}>{t("profile.changePassword")}</div>
          <p style={{ fontSize: "0.8rem", color: "var(--clr-text4)", marginBottom: "1rem" }}>
            {t("profile.passwordHelp")}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
            <div>
              <label style={labelStyle}>{t("profile.currentPassword")}</label>
              <input
                type="password"
                value={currentPwd}
                onChange={e => setCurrentPwd(e.target.value)}
                autoComplete="current-password"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("profile.newPassword")}</label>
              <input
                type="password"
                value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
                autoComplete="new-password"
                placeholder={t("profile.newPasswordPlaceholder")}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("profile.confirmPassword")}</label>
              <input
                type="password"
                value={confirmPwd}
                onChange={e => setConfirmPwd(e.target.value)}
                autoComplete="new-password"
                style={{
                  ...inputStyle,
                  borderColor: confirmPwd && newPwd && confirmPwd !== newPwd ? "#ef4444" : undefined,
                }}
              />
              {confirmPwd && newPwd && confirmPwd !== newPwd && (
                <p style={{ fontSize: "0.73rem", color: "#ef4444", marginTop: "0.3rem" }}>
                  {t("profile.passwordsNoMatch")}
                </p>
              )}
            </div>
          </div>

          {toast && <Toast msg={toast.msg} ok={toast.ok} />}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: "1.25rem",
              padding: "0.6rem 1.5rem",
              background: "var(--clr-org-primary)",
              color: "#fff",
              border: 0,
              borderRadius: 999,
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: "0.9rem",
              transition: "opacity 0.15s",
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? t("profile.saving") : t("profile.saveChanges")}
          </button>
        </div>
      </form>

      {/* Language card */}
      <div style={cardStyle}>
        <div style={sectionTitle}>{t("profile.language")}</div>
        <p style={{ fontSize: "0.8rem", color: "var(--clr-text4)", marginBottom: "1rem" }}>
          {t("profile.languageDesc")}
        </p>
        <div style={{ display: "flex", gap: "0.6rem" }}>
          {(["es", "en"] as Lang[]).map(l => {
            const active = lang === l;
            return (
              <button
                key={l}
                type="button"
                onClick={() => changeLang(l)}
                style={{
                  flex: 1,
                  padding: "0.6rem 1rem",
                  borderRadius: 12,
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: active ? 600 : 500,
                  color: active ? "#fff" : "var(--clr-text3)",
                  background: active ? "var(--clr-org-primary)" : "var(--clr-input-bg)",
                  border: active ? "1px solid transparent" : "1px solid var(--clr-border)",
                  transition: "all 0.15s",
                }}
              >
                {l === "es" ? t("profile.spanish") : t("profile.english")}
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: "0.73rem", color: "var(--clr-text4)", marginTop: "0.7rem" }}>
          {t("profile.languageNote")}
        </p>
      </div>

      <div style={{ marginTop: "1.75rem" }}>
        <TwoFactorCard />
      </div>
    </div>
  );
}
